import * as Tooltip from "@radix-ui/react-tooltip";
import { Inbox, Upload } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DocumentEntityType, DocumentType } from "../../../api/caseDocuments";
import { useAppSelector } from "../../../app/store";
import ButtonV2 from "../../../components/ui/button/v2";
import { selectSortedDocuments } from "../logic/caseDocumentsSelector";
import Document from "./Document";
import UploadDocumentModal from "./uploadDocumentModal";

interface DocumentSectionProps {
  documentType: DocumentType;
  isSingleton: boolean;
}

type DocumentSectionConfig = {
  label: string;
  emptyTitle: string;
  emptySubtitle: string;
  emptySubtitleClassName?: string;
  hideDocumentTypePill?: boolean;
  singletonDisabledTooltipText?: string;
};

const DOCUMENT_SECTION_CONFIG: Partial<Record<DocumentType, DocumentSectionConfig>> =
  {
    [DocumentType.INVESTIGATION_REPORT]: {
      label: "Investigation Reports",
      emptyTitle: "No Investigation Report",
      emptySubtitle: "Upload the Investigation Report in the board-approved format",
      hideDocumentTypePill: true,
      singletonDisabledTooltipText:
        "You can upload only one investigation report",
    },
    [DocumentType.INVESTIGATION_SUPPORTING]: {
      label: "Supporting Documents",
      emptyTitle: "No Supporting Documents",
      emptySubtitle:
        "Upload evidences, sanction letters, valuation reports etc. as applicable",
      emptySubtitleClassName: "w-[417px]",
      hideDocumentTypePill: true,
    },
  };

const ButtonV2Upload = ({
  onClick,
  disabled,
}: {
  onClick?: () => void;
  disabled?: boolean;
}) => {
  return (
    <ButtonV2
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      prefix={<Upload className="w-3 h-3 text-[#3551F3]" />}
      textClassName="font-sans font-small text-xs leading-5 text-[#3551F3]"
      className="px-0"
    >
      Upload
    </ButtonV2>
  );
};

 const DocumentSection = ({
  documentType,
  isSingleton,
}: DocumentSectionProps) => {
  const { id: caseId } = useParams<{ id: string }>();

  const documents = useAppSelector(selectSortedDocuments);

  const docsForType = useMemo(
    () => documents.filter((doc) => doc.document_type === documentType),
    [documents, documentType]
  );

  const config: DocumentSectionConfig = useMemo(() => {
    const fromMap = DOCUMENT_SECTION_CONFIG[documentType];
    return (
      fromMap ?? {
        label: documentType,
        emptyTitle: "No Documents",
        emptySubtitle: "No documents attached yet",
      }
    );
  }, [documentType]);

  const isUploadDisabled = isSingleton && docsForType.length >= 1;

  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const openUpload = useCallback(() => {
    setIsUploadOpen(true);
  }, []);

  const closeUpload = useCallback(() => {
    setIsUploadOpen(false);
  }, []);

  const renderUploadButton = () => {
    if (!isUploadDisabled) {
      return <ButtonV2Upload onClick={openUpload} />;
    }

    const tooltipText = config.singletonDisabledTooltipText;
    if (!tooltipText) {
      return <ButtonV2Upload disabled onClick={openUpload} />;
    }

    return (
      <Tooltip.Provider delayDuration={200}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <span className="inline-flex">
              <ButtonV2Upload disabled onClick={openUpload} />
            </span>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="bottom"
              sideOffset={4}
              className="z-[9999] w-[277px] max-w-[384px] px-3 py-[6px] rounded-md bg-[#374151] text-[#FAFAFA] text-xs font-normal leading-4"
            >
              {tooltipText}
              <Tooltip.Arrow className="fill-[#374151]" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  };

  const renderEmptyState = () => {
    return (
      <div className="w-full flex flex-col items-center justify-center text-center py-10">
        <div className="w-9 h-9 p-[6px] border border-[#E5E5E5] rounded-[6px] flex items-center justify-center mb-2">
          <Inbox className="w-6 h-[15px] text-[#0A0A0A]" />
        </div>
        <div className="font-sans font-medium text-sm leading-5 text-[#0A0A0A] mb-2">
          {config.emptyTitle}
        </div>
        <div
          className={[
            "font-sans font-normal text-sm leading-5 text-[#737373]",
            config.emptySubtitleClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {config.emptySubtitle}
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (docsForType.length === 0) return renderEmptyState();
    return (
      <Document
        documents={docsForType}
        hideDocumentTypePill={config.hideDocumentTypePill}
      />
    );
  };

  const renderUploadModal = () => {
    if (!isUploadOpen || !caseId) return null;
    return (
      <UploadDocumentModal
        onClose={closeUpload}
        initialDocumentType={documentType}
        lockDocumentType={true}
        initialEntityType={DocumentEntityType.CASE}
        initialEntityId={caseId}
      />
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between h-5 w-full">
        <span className="font-sans font-medium text-xs leading-[100%] text-[#737373]">
          {config.label}
        </span>
        {renderUploadButton()}
      </div>

      <div className="w-full">{renderBody()}</div>

      {renderUploadModal()}
    </div>
  );
}

export default DocumentSection;